import { i18n } from "../../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import adminRoutes from '../../routes/adminRoutes'
import dateUtil from '../../utils/date.util'
import subscriberService from '../../services/subscriber.service'
import timezoneService from '../../services/timezone.service'
import permissionService from '../../services/permission.service'
import siteApi, { SiteCreateRequest } from '../../apis/site.api'
import subscriberApi, { SubscriberData } from '../../apis/subscriber.api'
import timezoneApi, { TimezoneData } from '../../apis/timezone.api'
import permissionApi, { PermissionData } from '../../apis/permission.api'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import Card from '../../components/Card'
import Row from '../../components/Row/Row'
import PageHeading from '../../components/PageHeading'
import InputText from '../../components/InputText'
import Select from '../../components/Select'
import DatePicker from '../../components/DatePicker/DatePicker'
import PermissionPicker from '../../components/PermissionPicker'
import CardButton from '../../components/CardButton'

interface SiteCreateProps extends RouteComponentProps {}

interface SiteCreateState {
  name: string
  subscriber: string
  prefix: string
  endAt: string
  capacity: string
  timezone: string
  address: string
  city: string
  state: string
  zip: string
  permissions: PermissionData[]
  allowedPermissions: PermissionData[]
  subscribers: SubscriberData[]
  timezones: TimezoneData[]
}

class SiteCreate
  extends Component<SiteCreateProps, SiteCreateState> {
  constructor (props: SiteCreateProps) {
    super(props)
    const date = dateUtil.now()
    this.state = {
      name: '',
      subscriber: '',
      prefix: '',
      endAt: date,
      capacity: '',
      timezone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      permissions: [],
      allowedPermissions: [],
      subscribers: [],
      timezones: []
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.handlePermissionsChange = this.handlePermissionsChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
  }

  get subscriberSelectOptions () {
    return subscriberService.selectOptions(this.state.subscribers)
  }

  get timezoneSelectOptions () {
    return timezoneService.selectOptions(this.state.timezones)
  }

  get allowedPermissionNames () {
    const { allowedPermissions } = this.state
    return permissionService.listNames(allowedPermissions)
  }

  get permissionNames () {
    const { permissions } = this.state
    return permissionService.listNames(permissions)
  }

  create () {
    const { name, subscriber, prefix, endAt, capacity } = this.state
    const { timezone, address, city, state, zip, permissions } = this.state
    const permissionIds = permissionService.listIds(permissions)
    const payload: SiteCreateRequest = {}
    if (name) payload.name = name
    if (subscriber) payload.subscriber = Number(subscriber)
    if (prefix) payload.prefix = prefix
    if (endAt) payload.endAt = endAt
    if (capacity) payload.capacity = Number(capacity)
    if (timezone) payload.timezone = Number(timezone)
    if (address) payload.address = address
    if (city) payload.city = city
    if (state) payload.state = state
    if (zip) payload.zip = zip
    if (permissionIds) payload.permissions = permissionIds
    siteApi.create(payload, site => {
      if (!site) return
      this.props.history.push(adminRoutes.sites)
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'name') this.setState({ name: value })
    else if (name === 'subscriber') this.setState({ subscriber: value })
    else if (name === 'prefix') this.setState({ prefix: value })
    else if (name === 'capacity') this.setState({ capacity: value })
    else if (name === 'timezone') this.setState({ timezone: value })
    else if (name === 'address') this.setState({ address: value })
    else if (name === 'city') this.setState({ city: value })
    else if (name === 'state') this.setState({ state: value })
    else if (name === 'zip') this.setState({ zip: value })
  }

  handleDateChange (name: string, date: string) {
    if (name === 'endAt') this.setState({ endAt: date })
  }

  handlePermissionsChange (permissionNames: string[]) {
    const { allowedPermissions } = this.state
    const permissions = permissionService.filterByNames(
      allowedPermissions,
      permissionNames
    )
    this.setState({ permissions })
  }

  handleCreateClick () {
    this.create()
  }

  listSubscribers () {
    subscriberApi.list(subscribers => {
      if (!subscribers) return
      this.setState({ subscribers })
    })
  }

  listTimezones () {
    timezoneApi.list(timezones => {
      if (!timezones) return
      this.setState({ timezones })
    })
  }

  listAllowedPermissions () {
    permissionApi.list(allowedPermissions => {
      if (!allowedPermissions) return
      this.setState({ allowedPermissions })
    })
  }

  componentDidMount () {
    this.listSubscribers()
    this.listTimezones()
    this.listAllowedPermissions()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading text={i18n("Create Site")} />
            <Row column={2}>
              <InputText
                name="name"
                value={this.state.name}
                label={i18n("Name")}
                onChange={this.handleInputChange} />
              <Select
                name="subscriber"
                value={this.state.subscriber}
                options={this.subscriberSelectOptions}
                label={i18n("Subscriber")}
                onChange={this.handleInputChange} />
              <InputText
                name="prefix"
                value={this.state.prefix}
                label={i18n("Prefix")}
                onChange={this.handleInputChange} />
              <DatePicker
                name="endAt"
                value={this.state.endAt}
                label={i18n("End at")}
                onChange={this.handleDateChange} />
              <InputText
                name="capacity"
                value={this.state.capacity}
                label={i18n("Capacity")}
                onChange={this.handleInputChange} />
              <Select
                name="timezone"
                value={this.state.timezone}
                options={this.timezoneSelectOptions}
                label={i18n("Timezone")}
                onChange={this.handleInputChange} />
              <InputText
                name="address"
                value={this.state.address}
                label={i18n("Address")}
                onChange={this.handleInputChange} />
              <InputText
                name="city"
                value={this.state.city}
                label={i18n("City")}
                onChange={this.handleInputChange} />
              <InputText
                name="state"
                value={this.state.state}
                label={i18n("State")}
                onChange={this.handleInputChange} />
              <InputText
                name="zip"
                value={this.state.zip}
                label={i18n("Zip")}
                onChange={this.handleInputChange} />
            </Row>
            <PermissionPicker
              allowed={this.allowedPermissionNames}
              permissions={this.permissionNames}
              onChange={this.handlePermissionsChange} />
            <CardButton
              proceedText={i18n("Create")}
              cancelText={i18n("Cancel")}
              onProceedClick={this.handleCreateClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}

export default withRouter(SiteCreate)
